import React, {
  ForwardedRef,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Dropdown, Stack } from "react-bootstrap";
import { FiatCurrency } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";
import { useSetAtom } from "jotai";

import "./CurrencySelectorDropdown.scss";
import DownArrowIcon from "../../icons/DownArrowIcon";
import { fetchIpInfo } from "../../../utils/helpers";
import { IpInfoCountry } from "../../../types";
import useExchangeRatesQuery from "../../../react-query/queries/use-exchange-rates";
import CustomModal from "../../CustomModal/CustomModal";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import WarningBadge from "../../WarningBadge/WarningBadge";
import useDisclosure from "../../../hooks/useDisclosure";
import WalletClosedIcon from "../../icons/WalletClosedIcon";
import { showSuccessToast } from "../../../utils/show-toast";
import { handleUpdateUser } from "../../../api-clients/userAccountClient";
import useAnalytics from "../../../hooks/useAnalytics";
import { userProfileAtom } from "../../../jotai/atoms/user";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";

interface CustomToggleProps {
  children: ReactNode;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const CustomToggle = React.forwardRef<HTMLButtonElement, CustomToggleProps>(
  ({ children, onClick }, ref: ForwardedRef<HTMLButtonElement>) => (
    <button type="button" ref={ref} onClick={(e) => onClick(e)} className="toggle">
      {children}
      <DownArrowIcon fill="white" />
    </button>
  ),
);

interface CurrencyChangeConfirmationModalProps extends GenericModalProps {
  newCurrency?: FiatCurrency;
}

function CurrencyChangeConfirmationModal({
  isOpen,
  onClose,
  newCurrency,
}: CurrencyChangeConfirmationModalProps) {
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  const analytics = useAnalytics();
  const [loading, setLoading] = useState<boolean>(false);
  const setUserProfile = useSetAtom(userProfileAtom);

  const updateUserCurrency = useCallback(async () => {
    setLoading(true);

    try {
      const userData = await handleUpdateUser({
        currency: newCurrency,
      });
      if (userData) {
        analytics.sendSelectCurrencyEvent(newCurrency);
        showSuccessToast("Currency updated!");
        setUserProfile(userData);
        onClose();
      }
    } finally {
      setLoading(false);
    }
  }, [analytics, newCurrency, onClose, setUserProfile]);

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      customClass="currency-change-confirmation-modal"
    >
      <Stack gap={4}>
        <Stack className="align-items-center main-content">
          <span className="wallet-icon-wrapper">
            <WalletClosedIcon />
          </span>
          <h2 className="main-copy">{t("confirm_currency")}</h2>
          <p className="sub-copy">
            {t("you_have_selected_currency_this_would_be_used_for", {
              currency: newCurrency ?? "",
            })}{" "}
            <span className="fw-bold">{t("deposits")}</span> {t("and")}{" "}
            <span className="fw-bold">{t("cash_outs")}</span>.
          </p>
        </Stack>
        <Stack gap={3}>
          <WarningBadge
            text={`${t("you_cannot_change_this_after_your_first_top_up")}.`}
            containerClassName="info-text-wrapper"
          />
          <PrimaryActionButton
            size="lg"
            text={
              loading ? (
                <CustomSpinner size="sm" />
              ) : (
                t("confirm_specific_currency", { currency: newCurrency ?? "" })
              )
            }
            onClick={updateUserCurrency}
          />
        </Stack>
      </Stack>
    </CustomModal>
  );
}

interface CurrencySelectorDropdownProps {
  selectedCurrency?: FiatCurrency;
  setSelectedCurrency: React.Dispatch<
    React.SetStateAction<FiatCurrency | undefined>
  >;
  showConfirmationModal?: boolean;
}

export default function CurrencySelectorDropdown({
  selectedCurrency,
  setSelectedCurrency,
  showConfirmationModal,
}: CurrencySelectorDropdownProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { query: exchangeRatesQuery } = useExchangeRatesQuery();
  const [temporarySelectedCurrency, setTemporarySelectedCurrency] =
    useState<FiatCurrency>();
  const rates = exchangeRatesQuery.data;
  const currencies = Object.keys(rates ?? {})
    .map((key) => key.toUpperCase())
    .filter((code): code is FiatCurrency => code in FiatCurrency);

  const handleCurrencySelection = (currency: FiatCurrency) => {
    if (showConfirmationModal) {
      setTemporarySelectedCurrency(currency);
      onOpen();
    } else {
      setSelectedCurrency(currency);
    }
  };

  useEffect(() => {
    if (selectedCurrency) return;

    const getCurrentCountry = async () => {
      const currentCountry = (await fetchIpInfo())?.country;
      if (currentCountry && currentCountry !== IpInfoCountry.NG) {
        setSelectedCurrency(FiatCurrency.USD);
        return;
      }
      setSelectedCurrency(FiatCurrency.NGN);
    };

    getCurrentCountry();
  }, [selectedCurrency, setSelectedCurrency]);

  if (!selectedCurrency) return null;

  return (
    <>
      <div className="d-flex justify-content-start">
        <Dropdown className="currency-dropdown">
          <Dropdown.Toggle
            as={CustomToggle}
            id="dropdown-basic"
            className="toggle"
          >
            {selectedCurrency}
          </Dropdown.Toggle>
          <Dropdown.Menu className="menu">
            {currencies.map((currency, i) => (
              <Dropdown.Item
                key={currency}
                onClick={() => handleCurrencySelection(currency)}
                className="item"
                style={{
                  color: currency === selectedCurrency ? "#6C53E8" : "white",
                  marginBottom: i === currencies.length - 1 ? 0 : 12,
                }}
              >
                {currency}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <CurrencyChangeConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        newCurrency={temporarySelectedCurrency}
      />
    </>
  );
}
